import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../components/AuthContext';
import EmojiLoader from '../components/EmojiLoader';
import { FiChevronDown, FiInfo } from 'react-icons/fi';
import './Wallet.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/* ---------- helpers ---------- */
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dmy = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

const dmyFromYMD = (ymdStr) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymdStr || ''));
  if (!m) return String(ymdStr || '');
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
};

const formatCurrency = (v) =>
  `R$ ${(Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function startEndOfMonth(d = new Date()) {
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end   = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { startDate: ymd(start), endDate: ymd(end), start, end };
}

/* ---------- pega ?as=... ---------- */
function getAsFromLocation() {
  if (typeof window === 'undefined') return null;
  const qs = new URLSearchParams(window.location.search);
  const as = qs.get('as') || qs.get('asUserId');
  if (as) return as;
  if (window.location.hash) {
    const qIndex = window.location.hash.indexOf('?');
    if (qIndex >= 0) {
      const fromHash = new URLSearchParams(window.location.hash.slice(qIndex)).get('as');
      if (fromHash) return fromHash;
    }
  }
  const m =
    window.location.pathname.match(/\/wallet\/view\/([a-f0-9]{24})/i) ||
    window.location.pathname.match(/\/wallet\/([a-f0-9]{24})/i);
  return m ? m[1] : null;
}

/* ---------- faturas ---------- */
const monthLabelPT = (p) => {
  const ym = p?.month;
  if (ym && /^\d{4}-\d{2}$/.test(ym)) {
    const [y, mo] = ym.split('-').map(Number);
    const dt = new Date(y, mo - 1, 1);
    return dt.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }
  if (p?.validationDate) {
    const dt = new Date(p.validationDate);
    return dt.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }
  return '—';
};

const statusInfo = (raw) => {
  const s = String(raw || '').toUpperCase();
  if (s === 'PAID') return { label: 'Pago', cls: 'paid' };
  if (s === 'PENDING') return { label: 'Pendente', cls: 'pending' };
  if (s === 'CANCELLED' || s === 'CANCELED') return { label: 'Cancelado', cls: 'cancelled' };
  return { label: s || '—', cls: 'neutral' };
};

function StatusBadge({ status }) {
  const { label, cls } = statusInfo(status);
  return <span className={`status-badge ${cls}`}>{label}</span>;
}

export default function Wallet() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  const [period, setPeriod] = useState(() => startEndOfMonth());
  const [summary, setSummary] = useState(null);
  const [validatedDays, setValidatedDays] = useState([]);
  const [payments, setPayments] = useState([]);

  const asUserId = useMemo(getAsFromLocation, []);
  const viewingAs = Boolean(asUserId);

  // dados das conversões por dia (cache) + estado de expansão e loading
  const [dayConvs, setDayConvs] = useState({});    // { 'YYYY-MM-DD': Array }
  const [expanded, setExpanded] = useState({});    // { 'YYYY-MM-DD': boolean }
  const [loadingDay, setLoadingDay] = useState({});// { 'YYYY-MM-DD': boolean }

  const fetchAll = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const baseParams = { startDate: period.startDate, endDate: period.endDate, t: Date.now(), ...(asUserId ? { asUserId } : {}) };

      const [s, v, p] = await Promise.allSettled([
        axios.get(`${API_BASE}/api/wallet/summary`,   { headers, params: baseParams }),
        axios.get(`${API_BASE}/api/wallet/validated`, { headers, params: baseParams }),
        axios.get(`${API_BASE}/api/wallet/payments`,  { headers, params: baseParams }),
      ]);

      setSummary(s.status === 'fulfilled' ? s.value.data : null);
      setValidatedDays(v.status === 'fulfilled' ? (v.value.data || []) : []);
      setPayments(p.status === 'fulfilled' ? (p.value.data || []) : []);
      setDayConvs({}); setExpanded({}); setLoadingDay({});
    } finally {
      setLoading(false);
    }
  }, [user?.token, period.startDate, period.endDate, asUserId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const receivableTotal = useMemo(() => Number(summary?.receivableTotal || 0), [summary]);

  const payableBuckets = useMemo(() => {
    if (Array.isArray(summary?.buckets) && summary.buckets.length) return summary.buckets;
    return [];
  }, [summary]);

  const viewAsInfo = useMemo(() => {
    if (summary?.viewAsUser) return summary.viewAsUser;
    if (viewingAs) return { id: asUserId, name: null, email: null };
    return null;
  }, [summary?.viewAsUser, viewingAs, asUserId]);

  if (loading) return <EmojiLoader />;

  return (
    <div className="wallet-container">
      <header className="wallet-header">
        <div>
          <h1>Carteira</h1>
          <p>Acompanhe o que você vai receber, as conversões validadas e o histórico de pagamentos.</p>

          {viewAsInfo && (
            <div style={{ marginTop: 8 }}>
              <span className="sales-pill" title={viewAsInfo.email || ''}>
                Visualizando como: {viewAsInfo.name || viewAsInfo.email || viewAsInfo.id}
              </span>
            </div>
          )}
        </div>

        <div className="wallet-actions">
          <div className="wallet-period">
            <button
              onClick={() => {
                const d = new Date(period.start);
                const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
                setPeriod(startEndOfMonth(prev));
              }}
              className="nav-month"
              aria-label="Mês anterior"
            >‹</button>
            <span className="wallet-period-label">
              {period.start.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
            </span>
            <button
              onClick={() => {
                const d = new Date(period.start);
                const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
                setPeriod(startEndOfMonth(next));
              }}
              className="nav-month"
              aria-label="Próximo mês"
            >›</button>
          </div>

          <a
            className="btn-ghost whats"
            href="https://wa.me/5511934459150?text=Oi%20equipe!%20Tenho%20uma%20d%C3%BAvida%20sobre%20pagamentos."
            target="_blank" rel="noreferrer"
          >
            💬 Suporte
          </a>
        </div>
      </header>

      {/* Resumo */}
      <section className="wallet-summary">
        <div className="summary-card">
          <div className="summary-top">
            <span className="summary-title">
              Comissões a receber
              <span className="tooltip kpi-i" style={{ marginLeft: 6 }}>
                <FiInfo size={14} className="info-icon" />
                <span className="tooltip-bubble">
                  Somamos apenas pedidos <b>concluídos</b> no período selecionado. Esses valores são pagos no mês seguinte.
                </span>
              </span>
            </span>
            <span className="summary-period">{dmy(period.start)} – {dmy(period.end)}</span>
          </div>

          <div className="summary-value">{formatCurrency(receivableTotal)}</div>

          {payableBuckets.length > 0 && (
            <div className="summary-buckets">
              {payableBuckets.map(b => (
                <div key={b.label} className="bucket">
                  <span className="bucket-label">Previsto p/ {b.label}</span>
                  <strong className="bucket-value">{formatCurrency(b.amount)}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Comissões validadas por dia */}
      <section className="wallet-validated">
        <h2>Comissões Validadas</h2>
        <div className="table-wrap">
          <table className="wallet-table">
            <thead>
              <tr>
                <th>Data de Validação</th>
                <th className="right">Comissão Total</th>
                <th>Informações</th>
                <th className="right">Ação</th>
              </tr>
            </thead>
            <tbody>
              {validatedDays.length === 0 && (
                <tr><td colSpan={4} className="empty">Sem validações neste período.</td></tr>
              )}

              {validatedDays.map((row) => {
                const isOpen = !!expanded[row.date];
                const items = dayConvs[row.date] || [];

                return (
                  <React.Fragment key={row.date}>
                    <tr>
                      <td>{dmyFromYMD(row.date)}</td>
                      <td className="right">{formatCurrency(row.total)}</td>
                      <td>{row.invoiceInfo || 'Comissões a receber'}</td>
                      <td className="right">
                        <button
                          className={`ghost small ${isOpen ? 'is-open' : ''}`}
                          aria-expanded={isOpen}
                          onClick={async () => {
                            if (!dayConvs[row.date]) {
                              try {
                                setLoadingDay((s) => ({ ...s, [row.date]: true }));
                                const params = { date: row.date, t: Date.now(), ...(asUserId ? { asUserId } : {}) };
                                const { data } = await axios.get(`${API_BASE}/api/wallet/conversions`, {
                                  headers: { Authorization: `Bearer ${user.token}` },
                                  params
                                });
                                setDayConvs((s) => ({ ...s, [row.date]: data || [] }));
                                setExpanded((s) => ({ ...s, [row.date]: true }));
                              } catch {
                                setDayConvs((s) => ({ ...s, [row.date]: [] }));
                                setExpanded((s) => ({ ...s, [row.date]: true }));
                              } finally {
                                setLoadingDay((s) => ({ ...s, [row.date]: false }));
                              }
                            } else {
                              setExpanded((s) => ({ ...s, [row.date]: !s[row.date] }));
                            }
                          }}
                          title="Ver conversões"
                        >
                          {loadingDay[row.date] ? (
                            <>
                              <span className="mini-spinner" />
                              Carregando…
                            </>
                          ) : (
                            <>
                              {isOpen ? 'Esconder' : 'Ver conversões'}
                              <FiChevronDown className="chev" />
                            </>
                          )}
                        </button>
                      </td>
                    </tr>

                    {/* Linha única com colSpan e animação (não mexe nas colunas do cabeçalho) */}
                    <tr className="conv-holder">
                      <td colSpan={4}>
                        <div className={`conv-accordion ${isOpen ? 'open' : ''}`}>
                          <div className="conv-list">
                            {items.length === 0 && !loadingDay[row.date] && (
                              <div className="conv-row conv-empty">Nenhuma conversão encontrada para este dia.</div>
                            )}

                            {items.map((conv, i) => (
                              <div key={`${row.date}-${i}`} className="conv-row">
                                <div className="conv-item">
                                  {!!conv.imageUrl && (
                                    <img
                                      src={conv.imageUrl}
                                      alt=""
                                      onError={(e)=>e.currentTarget.style.display='none'}
                                    />
                                  )}
                                  <div className="conv-meta">
                                    <div className="conv-title">{conv.productName || 'Item'}</div>
                                    <div className="conv-sub">
                                      Pedido {conv.orderId} • {conv.shopName || 'Loja'} • {conv.quantity || 1}x
                                    </div>
                                  </div>
                                </div>

                                <div className="conv-right">
                                  <span className="conv-status">{(conv.statusLabel || conv.status || '').toString()}</span>
                                  <strong className="conv-amount">{formatCurrency(conv.commission)}</strong>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Histórico de pagamentos */}
      <section className="wallet-history">
        <h2>Histórico de Pagamento</h2>
        <div className="table-wrap">
          <table className="wallet-table">
            <thead>
              <tr>
                <th>Período da Fatura</th>
                <th>ID da fatura</th>
                <th className="right">Bruto</th>
                <th className="right">Taxa Plataforma</th>
                <th className="right">Líquido</th>
                <th>Status</th>
                <th>Data de Pagamento</th>
                <th className="right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && (
                <tr><td colSpan={8} className="empty">Sem pagamentos encontrados.</td></tr>
              )}
              {payments.map((p, idx) => (
                <tr key={idx}>
                  <td>{monthLabelPT(p)}</td>
                  <td>{p.invoiceId || '—'}</td>
                  <td className="right">{p.gross != null ? formatCurrency(p.gross) : '—'}</td>
                  <td className="right">{p.platformFee != null ? `- ${formatCurrency(p.platformFee)}` : '—'}</td>
                  <td className="right">{formatCurrency(p.amount)}</td>
                  <td><StatusBadge status={p.status} /></td>
                  <td>{p.paidAt ? new Date(p.paidAt).toLocaleDateString('pt-BR') : '—'}</td>
                  <td className="right">
                    {p.invoiceUrl ? (
                      <a className="link" href={p.invoiceUrl} target="_blank" rel="noreferrer">Abrir fatura</a>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
